import type { AuthPromptRequest } from '../contexts/AuthPromptContext';

/** sessionStorage key holding the action a guest asked for before leaving for Google. */
export const PENDING_SIGN_IN_ACTION_STORAGE_KEY = 'belhop_pending_sign_in_action';
/** How long a stored action stays valid. */
export const PENDING_SIGN_IN_ACTION_MAX_AGE_MS = 10 * 60 * 1000;
/** Longest listing name kept for the announcement. */
export const PENDING_SIGN_IN_ACTION_NAME_MAX_LENGTH = 120;

export interface PendingSignInListing {
  kind: 'accommodation' | 'service';
  id: number;
  name?: string;
}

export type PendingSignInAction =
  { kind: 'wishlist'; listing: PendingSignInListing } | { kind: 'checkout' };

const LISTING_KINDS: readonly PendingSignInListing['kind'][] = ['accommodation', 'service'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

function parseListing(value: unknown): PendingSignInListing | null {
  if (!isRecord(value)) return null;
  const { kind, id, name } = value;
  if (!LISTING_KINDS.includes(kind as PendingSignInListing['kind'])) return null;
  if (typeof id !== 'number' || !Number.isSafeInteger(id) || id <= 0) return null;
  const listing: PendingSignInListing = { kind: kind as PendingSignInListing['kind'], id };
  if (
    typeof name === 'string' &&
    name.length > 0 &&
    name.length <= PENDING_SIGN_IN_ACTION_NAME_MAX_LENGTH
  ) {
    listing.name = name;
  }
  return listing;
}

function parseAction(value: unknown, nowMs: number): PendingSignInAction | null {
  if (!isRecord(value) || typeof value.storedAt !== 'number') return null;
  const age = nowMs - value.storedAt;
  if (age < 0 || age > PENDING_SIGN_IN_ACTION_MAX_AGE_MS) return null;
  if (value.kind === 'checkout') return { kind: 'checkout' };
  if (value.kind !== 'wishlist') return null;
  const listing = parseListing(value.listing);
  return listing ? { kind: 'wishlist', listing } : null;
}

function truncatedListing({ kind, id, name }: PendingSignInListing): PendingSignInListing {
  const listing: PendingSignInListing = { kind, id };
  if (name) listing.name = name.slice(0, PENDING_SIGN_IN_ACTION_NAME_MAX_LENGTH);
  return listing;
}

/** Remembers the action across the Google redirect; a long listing name is truncated. */
export function storePendingSignInAction(action: PendingSignInAction): void {
  if (typeof window === 'undefined') return;
  const stored =
    action.kind === 'checkout'
      ? { kind: action.kind }
      : { kind: action.kind, listing: truncatedListing(action.listing) };
  window.sessionStorage.setItem(
    PENDING_SIGN_IN_ACTION_STORAGE_KEY,
    JSON.stringify({ ...stored, storedAt: Date.now() }),
  );
}

/**
 * Reads and removes the stored action in one step; removal happens on every call, whatever
 * the value holds. Returns a new object built only from fields that passed validation.
 */
export function consumePendingSignInAction(): PendingSignInAction | null {
  if (typeof window === 'undefined') return null;
  const stored = window.sessionStorage.getItem(PENDING_SIGN_IN_ACTION_STORAGE_KEY);
  window.sessionStorage.removeItem(PENDING_SIGN_IN_ACTION_STORAGE_KEY);
  if (stored === null) return null;
  try {
    return parseAction(JSON.parse(stored), Date.now());
  } catch {
    return null;
  }
}

export function discardPendingSignInAction(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(PENDING_SIGN_IN_ACTION_STORAGE_KEY);
}

/** The action a pop-up request asks to finish after sign-in; null when it asks for none. */
export function pendingSignInActionFor(request: AuthPromptRequest): PendingSignInAction | null {
  if (request.intent === 'checkout') return { kind: 'checkout' };
  if (request.intent !== 'wishlist') return null;
  const { kind, id, name } = request.item;
  return { kind: 'wishlist', listing: name ? { kind, id, name } : { kind, id } };
}
